import React, { forwardRef, Fragment, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { downloadCsvFile, toCSV } from 'common/utils/helpers';
import { useGetCountersQuery } from 'apis/pm';
import { ProgressIndicator } from 'components/shared/Progress';
import { DeviceTimeSeriesChart, DeviceChartError } from 'components/shared/DeviceChart';
import { useChartPreference, useChartTime, useActionAnnotations, useUserAnnotations } from '../../hooks';
import DeviceChartMenuBar from './DeviceChartMenuBar';
import styles from './DeviceChartCard.module.css';
import { getCounterSuffix } from '../../utils';
import { isGa2Device } from 'common/utils/deviceTwinUtils';

const DeviceChartCard = forwardRef(function DeviceChartCard(
  {
    name,
    meta,
    query: requestedQuery,
    classes,
    enabledZoom,
    enabledMarker,
    displayAxisTitle,
    displayActionAnnotation,
    displayUserAnnotation,
    displayDataLabel,
    displayMarker,
    onEdit,
    onChange,
    onDelete,
    onMarkerPlaceEnd,
    onMarkerPlaceCancel
  },
  forwardedRef
) {
  const { title, query, datasets, scales, isLoaded, isLocal } = useChartPreference(meta, requestedQuery);
  const { devices, counters, actions } = query;
  const { ref, from, to, resolution, onResize, onZoomComplete } = useChartTime({ enabled: isLoaded }, forwardedRef);
  const isReady = isLoaded && resolution != null;
  const activeTabDeviceId = useSelector((state) => state.ui.pivotOverviewPage.activeTab);
  const donor = useSelector((state) => state.ui.singleDevicePage.selectedDevice);

  const countersQuery = useGetCountersQuery(
    {
      devices: isGa2Device(donor) ? [donor.id]: devices,
      counters,
      from,
      to,
      interval: resolution,
      suffix: getCounterSuffix(donor, activeTabDeviceId)
    },
    {
      enabled: isReady,
      placeholderData: [],
      retry: (failureCount, error) => error?.response?.status !== 500 && failureCount < 3
    }
  );
  const {
    data: actionAnnotations,
    isFetching: isActionAnnotationsFetching,
    isRebootVisible,
    toggleRebootVisible
  } = useActionAnnotations({
    name,
    devices,
    actions,
    from,
    to,
    enabled: displayActionAnnotation && isReady
  });
  const {
    data: userAnnotations,
    isFetching: isUserAnnotationsFetching,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation
  } = useUserAnnotations({
    devices,
    from,
    to,
    enabled: displayUserAnnotation && isReady
  });
  const handleExportData = useCallback(() => {
    const csv = toCSV(
      [
        {
          name: 'timestamp',
          accessor: (row) => row.timestamp && new Date(row.timestamp).toISOString()
        },
        ...counters
      ],
      countersQuery.data
    );
    downloadCsvFile(csv, 'chartExportData.csv');
  }, [counters, countersQuery.data]);

  return (
    <DeviceChartCardContainer
      role="region"
      aria-label="Device Chart"
      aria-busy={countersQuery.isFetching || isActionAnnotationsFetching || isUserAnnotationsFetching}
      className={classes?.container}
      error={countersQuery.error && <DeviceChartError retrying={countersQuery.isFetching} classes={classes} />}
    >
      <DeviceChartCardHeader className={classes?.header}>
        <DeviceChartMenuBar
          name={name}
          title={title}
          onChange={onChange}
          actions={
            <Fragment>
              <button type="button" onClick={onEdit}>
                Edit
              </button>
              <button type="button" disabled={isLocal} onClick={onDelete}>
                Delete
              </button>
              <button
                type="button"
                className={styles.export}
                disabled={countersQuery.isFetching}
                onClick={handleExportData}
              >
                Export Data
              </button>
            </Fragment>
          }
        />
      </DeviceChartCardHeader>
      <DeviceChartCardBody className={classes?.body}>
        {countersQuery.isFetching && <ProgressIndicator className={styles.progress} />}
        {isLoaded && (
          <DeviceTimeSeriesChart
            ref={ref}
            title={`${title} graph`}
            data={countersQuery.data}
            actionAnnotations={actionAnnotations}
            userAnnotations={userAnnotations}
            datasets={datasets}
            scales={scales}
            actionsLoading={isActionAnnotationsFetching}
            actionsEnabled={displayActionAnnotation && actions?.length > 0}
            actionsVisible={isRebootVisible}
            enabledZoom={enabledZoom}
            enabledMarker={enabledMarker}
            displayAxisTitle={displayAxisTitle}
            displayActionAnnotation={displayActionAnnotation && isRebootVisible}
            displayUserAnnotation={displayUserAnnotation}
            displayDataLabel={displayDataLabel}
            displayMarker={displayMarker}
            onClickActionVisible={toggleRebootVisible}
            onCreateAnnotation={createAnnotation}
            onUpdateAnnotation={updateAnnotation}
            onDeleteAnnotation={deleteAnnotation}
            onResize={onResize}
            onZoomComplete={onZoomComplete}
            onMarkerPlaceEnd={onMarkerPlaceEnd}
            onMarkerPlaceCancel={onMarkerPlaceCancel}
          />
        )}
      </DeviceChartCardBody>
    </DeviceChartCardContainer>
  );
});

function DeviceChartCardContainer({ error, className, children, ...rest }) {
  return (
    <div className={styles.root}>
      {error != null ? (
        error
      ) : (
        <div className={classNames(styles.container, className)} {...rest}>
          {children}
        </div>
      )}
    </div>
  );
}

DeviceChartCard.Container = DeviceChartCardContainer;

function DeviceChartCardHeader({ className, children, ...rest }) {
  return (
    <div className={classNames(styles.header, className)} {...rest}>
      {children}
    </div>
  );
}

DeviceChartCard.Header = DeviceChartCardHeader;

function DeviceChartCardBody({ className, children, ...rest }) {
  return (
    <div className={classNames(styles.body, className)} {...rest}>
      {children}
    </div>
  );
}

DeviceChartCard.Body = DeviceChartCardBody;

export default DeviceChartCard;
